import React from 'react';
import { motion } from 'framer-motion';
import { AlertCircleIcon, BatteryWarningIcon, BrainIcon } from 'lucide-react';
export function PainPoints() {
  const scrollToPurchase = () => {
    document.getElementById('compra')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  return <section className="py-20 bg-[#FDF8F4]" data-id="element-56">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-57">
        {/* Initial Hook */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="text-center mb-16" data-id="element-58">
          <h2 className="text-3xl font-bold text-gray-900 mb-6" data-id="element-59">
            Si trabajás con niños, esto no te pasa "a veces".
          </h2>
          <p className="text-2xl text-[#E8614D] font-bold mb-8" data-id="element-60">
            Te pasa todo el tiempo.
          </p>

          <div className="bg-white p-8 rounded-xl shadow-sm space-y-6 text-left border border-gray-100" data-id="element-61">
            <p className="text-lg text-gray-700 italic border-l-4 border-gray-300 pl-4" data-id="element-62">
              "Terminás una sesión y pensás: Hice mucho… pero ¿qué logré
              exactamente?"
            </p>
            <p className="text-lg text-gray-700 italic border-l-4 border-gray-300 pl-4" data-id="element-63">
              "¿Cómo justifico este proceso?"
            </p>
            <p className="text-lg text-gray-700 italic border-l-4 border-gray-300 pl-4" data-id="element-64">
              "¿Estoy avanzando o solo sosteniendo?"
            </p>
          </div>
        </motion.div>

        {/* The Cycle */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="mb-16" data-id="element-65">
          <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center" data-id="element-66">
            Llegás a la siguiente sesión y:
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center" data-id="element-67">
            {['Ajustás sobre la marcha', 'Cambiás actividades', 'Improvisás más de lo que te gustaría'].map((item, i) => <div key={i} className="bg-white p-6 rounded-lg shadow-sm border-t-4 border-[#0D7377]" data-id="element-68">
                <AlertCircleIcon className="w-8 h-8 text-[#0D7377] mx-auto mb-4" data-id="element-69" />
                <p className="font-medium text-gray-800" data-id="element-70">{item}</p>
              </div>)}
          </div>
          <p className="text-center mt-8 text-lg text-gray-700 font-medium" data-id="element-71">
            No porque no sepas. Sino porque no siempre tenés una estructura que
            te sostenga.
          </p>
        </motion.div>

        {/* Burnout Section */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="bg-gray-900 text-white p-8 md:p-12 rounded-2xl shadow-xl relative overflow-hidden" data-id="element-72">
          <div className="absolute top-0 right-0 w-64 h-64 bg-[#E8614D] opacity-10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" data-id="element-73" />

          <div className="relative z-10" data-id="element-74">
            <div className="flex items-center gap-4 mb-6" data-id="element-75">
              <BatteryWarningIcon className="w-10 h-10 text-[#E8614D]" data-id="element-76" />
              <h3 className="text-2xl font-bold" data-id="element-77">
                El desgaste real no es físico
              </h3>
            </div>

            <p className="text-xl text-gray-300 mb-8 font-medium" data-id="element-78">
              Es mental y silencioso
            </p>

            <ul className="space-y-4 mb-8" data-id="element-79">
              {['Pensar cada sesión desde cero', 'Adaptar sobre la marcha niños con TDAH que no sostienen la atención', 'Ajustar para niños con TEA que necesitan anticipación, estructura y claridad', 'Manejar desregulación, ansiedad, frustración', 'Explicar a familias que "el proceso lleva tiempo" sin tener registros claros'].map((item, i) => <li key={i} className="flex items-start gap-3" data-id="element-80">
                  <div className="w-1.5 h-1.5 bg-[#E8614D] rounded-full mt-2.5 flex-shrink-0" data-id="element-81" />
                  <span className="text-gray-300" data-id="element-82">{item}</span>
                </li>)}
            </ul>

            <p className="text-lg font-medium text-white border-t border-gray-700 pt-6" data-id="element-83">
              Y todo eso, sesión tras sesión.
            </p>
          </div>
        </motion.div>

        {/* The Real Problem */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="mt-16 text-center" data-id="element-84">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-teal-100 rounded-full mb-6" data-id="element-85">
            <BrainIcon className="w-8 h-8 text-[#0D7377]" data-id="element-86" />
          </div>
          <h3 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-87">
            El problema no es tu formación
          </h3>
          <p className="text-xl text-gray-600 mb-8" data-id="element-88">
            Es trabajar sin un sistema que ordene la intervención
          </p>

          <div className="bg-white p-8 rounded-xl border border-gray-200 shadow-sm max-w-2xl mx-auto" data-id="element-89">
            <p className="font-bold text-gray-800 mb-4" data-id="element-90">
              Las fonoaudiólogas no se cansan de trabajar con niños. Se cansan
              de:
            </p>
            <ul className="text-left space-y-3" data-id="element-91">
              {['Dudar en silencio', 'Sentir que el trabajo no se ve', 'No poder mostrar avances con claridad', 'Cargar solas con todo el proceso clínico'].map((item, i) => <li key={i} className="flex items-center gap-3 text-gray-600" data-id="element-92">
                  <span className="text-[#E8614D]" data-id="element-93">✖</span>
                  {item}
                </li>)}
            </ul>
          </div>

          <div className="mt-12" data-id="element-94">
            <a href="https://maestros.ebookditales.com/cart/44768212975752:1" target="_blank" rel="noopener noreferrer" className="inline-block bg-[#22C55E] text-white px-8 py-4 rounded-lg font-bold text-lg shadow-lg hover:bg-[#16A34A] transition-all hover:scale-105 no-underline" data-id="element-95">
              Quiero trabajar con este sistema profesional
            </a>
          </div>
        </motion.div>
      </div>
    </section>;
}